import {
    Alert,
    Autocomplete,
    Box,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    Tooltip
} from "@mui/material";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import KeyIcon from "@mui/icons-material/Key";
import BlockIcon from "@mui/icons-material/Block";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useMutation, useQuery, useQueryClient} from "react-query";
import {useSnackbar} from "notistack";
import {SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import type {User} from "crqlar-react-common/dist/models/identity/user.model";
import {ClientUserService} from "crqlar-react-common/dist/services/user/clientUser.service";
import {useOrganizationApps} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {useAllSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {useAllRootResourcesForSector} from "crqlar-react-common/dist/utils/hooks/resources/useAllRootResources";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import {buildTillsSnippet, type PosDevice, PosDeviceService} from "../../../../pos/posDevice.service";

const QUERY_KEY = "posDevices";

const userLabel = (user?: User) =>
    user ? ([user.firstName, user.lastName].filter(Boolean).join(" ") || user.email || user.id || "") : "";

/**
 * Tills that open crqlar inside the Simphony POS, signed in and straight on a floor plan.
 *
 * Each till gets a device key; Oracle EMC holds only that key (in the `crqlarTills` content of the
 * `CRQLAR` extension application), and everything tenant-specific — the organization, the user the
 * till acts as, and which restaurant's floor plan it opens — lives here. That is what lets every
 * property run the same EMC script. The key is shown once, together with the snippet to paste.
 *
 * See `features/simphony-pos-tills-screen.md`.
 */
const SimphonyPosTills = () => {

    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const {enqueueSnackbar} = useSnackbar();

    const devicesQuery = useQuery(QUERY_KEY, () => PosDeviceService.list());
    const devices = devicesQuery.data?.content ?? [];

    const usersQuery = useQuery("posTillUsers", () => ClientUserService.getAllUsers({
        limit: {offset: 0, limit: 1000, sort: [{field: "firstName", direction: "ASC"}]}
    }));
    const users = usersQuery.data?.content ?? [];

    const restaurantApps = useRestaurantApps();

    const [editing, setEditing] = useState<PosDevice | undefined>();
    const [issued, setIssued] = useState<PosDevice | undefined>();

    const refresh = () => queryClient.invalidateQueries(QUERY_KEY);

    const save = useMutation(async (device: PosDevice) => {
        return device.id ? PosDeviceService.update(device) : PosDeviceService.add(device);
    }, {
        onSuccess: saved => {
            setEditing(undefined);
            if (saved.deviceKey) setIssued(saved);
            void refresh();
        },
        onError: () => { enqueueSnackbar(t("Could not save the till."), {variant: "error"}); }
    });

    const rotate = useMutation((id: string) => PosDeviceService.rotateKey(id), {
        onSuccess: saved => {
            setIssued(saved);
            void refresh();
        },
        onError: () => { enqueueSnackbar(t("Could not issue a new key."), {variant: "error"}); }
    });

    const revoke = useMutation((id: string) => PosDeviceService.revoke(id), {
        onSuccess: () => void refresh(),
        onError: () => { enqueueSnackbar(t("Could not revoke the till."), {variant: "error"}); }
    });

    const landingLabel = (device: PosDevice) => {
        if (device.landingPath) return device.landingPath;
        const app = restaurantApps.find(a => a.id === device.landingAppId);
        return app ? app.name : t("Automatic");
    };

    return (
        <Stack gap={2}>
            <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} gap={2}>
                <Box>
                    <Typography variant={"h6"}>{t("POS tills")}</Typography>
                    <Typography variant={"body2"} color={"text.secondary"}>
                        {t("Each till opens crqlar in the CRQLAR tab of the POS, already signed in and on the floor plan chosen here. EMC only stores the till's key.")}
                    </Typography>
                </Box>
                <Button variant={"contained"} startIcon={<AddIcon/>} onClick={() => setEditing({})}>
                    {t("Add till")}
                </Button>
            </Stack>

            {devicesQuery.isSuccess && devices.length === 0 &&
                <Alert severity={"info"}>{t("No tills yet. Add one to get the snippet for EMC.")}</Alert>}

            {devices.map(device => (
                <Stack
                    key={device.id}
                    direction={{xs: "column", sm: "row"}}
                    alignItems={{sm: "center"}}
                    gap={1}
                    sx={{border: 1, borderColor: "divider", borderRadius: 2, p: 2}}
                >
                    <Box sx={{flex: 1, minWidth: 0}}>
                        <Stack direction={"row"} gap={1} alignItems={"center"}>
                            <Typography fontWeight={600}>{device.name || t("Unnamed till")}</Typography>
                            {device.workstationName && <Chip size={"small"} label={device.workstationName}/>}
                        </Stack>
                        <Typography variant={"body2"} color={"text.secondary"}>
                            {t("Opens")}: {landingLabel(device)}
                            {" · "}{t("Signs in as")}: {userLabel(users.find(u => u.id === device.userId)) || "-"}
                            {" · "}{t("Key")}: {device.deviceKeyPub}
                        </Typography>
                        <Typography variant={"body2"} color={"text.secondary"}>
                            {t("Last seen")}: {device.lastSeenAt
                            ? `${formatFullDateTime(new Date(device.lastSeenAt))}${device.lastSeenWorkstation ? ` (${device.lastSeenWorkstation})` : ""}`
                            : t("never")}
                        </Typography>
                    </Box>
                    <Stack direction={"row"}>
                        <Tooltip title={t("Edit")}>
                            <IconButton onClick={() => setEditing(device)}><EditOutlinedIcon/></IconButton>
                        </Tooltip>
                        <Tooltip title={t("Issue a new key (the old one stops working immediately)")}>
                            <IconButton
                                disabled={rotate.isLoading}
                                onClick={() => {
                                    if (window.confirm(t("The till stops working until the new key is in EMC. Continue?"))) {
                                        rotate.mutate(device.id!);
                                    }
                                }}
                            >
                                <KeyIcon/>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t("Revoke")}>
                            <IconButton
                                color={"error"}
                                disabled={revoke.isLoading}
                                onClick={() => {
                                    if (window.confirm(t("Revoke this till? Its key stops working for good."))) {
                                        revoke.mutate(device.id!);
                                    }
                                }}
                            >
                                <BlockIcon/>
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>
            ))}

            {editing &&
                <PosTillDialog
                    device={editing}
                    users={users}
                    restaurantApps={restaurantApps}
                    saving={save.isLoading}
                    onClose={() => setEditing(undefined)}
                    onSave={device => save.mutate(device)}
                />}

            {issued &&
                <PosTillKeyDialog device={issued} onClose={() => setIssued(undefined)}/>}
        </Stack>
    );
};

interface RestaurantApp {
    id: string;
    name: string;
    sectorId: string;
}

/** Organization apps whose sector is a restaurant — the only sector with a floor plan. */
function useRestaurantApps(): RestaurantApp[] {
    const apps = useOrganizationApps();
    const sectors = useAllSectors();

    return useMemo(() => {
        const restaurantSectorIds = new Set(
            sectors.filter(s => s.sectorType === SectorType.RESTAURANT).map(s => s.id)
        );
        return apps
            .filter(app => !!app.id && restaurantSectorIds.has(app.configuration?.sectorId))
            .map(app => ({id: app.id!, name: app.name ?? "", sectorId: app.configuration.sectorId as string}));
    }, [apps, sectors]);
}

interface DialogProps {
    device: PosDevice;
    users: User[];
    restaurantApps: RestaurantApp[];
    saving: boolean;
    onClose: () => void;
    onSave: (device: PosDevice) => void;
}

function PosTillDialog({device, users, restaurantApps, saving, onClose, onSave}: DialogProps) {
    const {t} = useTranslation();
    const {localize} = useLocalize();

    const [draft, setDraft] = useState<PosDevice>(() => ({
        ...device,
        // A single restaurant app is the obvious choice; preselect it so a one-outlet hotel just saves.
        landingAppId: device.landingAppId ?? (!device.id && restaurantApps.length === 1 ? restaurantApps[0].id : undefined)
    }));

    const selectedApp = restaurantApps.find(a => a.id === draft.landingAppId);
    const restaurants = useAllRootResourcesForSector(selectedApp?.sectorId);

    const valid = !!draft.name?.trim() && !!draft.userId && !!draft.workstationName?.trim();

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth={"sm"}>
            <DialogTitle>{device.id ? t("Edit till") : t("Add till")}</DialogTitle>
            <DialogContent>
                <Stack gap={2.5} sx={{pt: 1}}>
                    <TextField
                        label={t("Name")}
                        placeholder={t("Restaurant — till 1")}
                        value={draft.name ?? ""}
                        onChange={e => setDraft({...draft, name: e.target.value})}
                        required
                    />
                    <TextField
                        label={t("Simphony workstation name")}
                        helperText={t("Exactly as shown on the till's status bar, e.g. SIMPHONY-POS.")}
                        value={draft.workstationName ?? ""}
                        onChange={e => setDraft({...draft, workstationName: e.target.value})}
                        required
                    />
                    <Autocomplete
                        options={users}
                        value={users.find(u => u.id === draft.userId) ?? null}
                        getOptionLabel={u => `${userLabel(u)}${u.email ? ` (${u.email})` : ""}`}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        onChange={(_, user) => setDraft({...draft, userId: user?.id})}
                        renderInput={params => (
                            <TextField
                                {...params}
                                required
                                label={t("Signs in as")}
                                helperText={t("Use a restricted user made for this till, never a manager account — anyone with EMC access can read the key.")}
                            />
                        )}
                    />
                    <TextField
                        select
                        label={t("Opens floor plan of")}
                        value={draft.landingAppId ?? ""}
                        onChange={e => setDraft({
                            ...draft,
                            landingAppId: e.target.value || undefined,
                            landingRestaurantId: undefined
                        })}
                        helperText={t("Automatic uses the only restaurant app, if there is exactly one.")}
                    >
                        <MenuItem value={""}>{t("Automatic")}</MenuItem>
                        {restaurantApps.map(app => <MenuItem key={app.id} value={app.id}>{app.name}</MenuItem>)}
                    </TextField>
                    {selectedApp &&
                        <TextField
                            select
                            label={t("Restaurant")}
                            value={draft.landingRestaurantId ?? ""}
                            onChange={e => setDraft({...draft, landingRestaurantId: e.target.value || undefined})}
                        >
                            <MenuItem value={""}>{t("First restaurant")}</MenuItem>
                            {restaurants.map(resource => (
                                <MenuItem key={resource.id} value={resource.id}>
                                    {localize(resource.nameLocalized) || resource.name}
                                </MenuItem>
                            ))}
                        </TextField>}
                    {!!draft.landingPath &&
                        <Alert severity={"info"}>
                            {t("This till has a fixed landing path that overrides the choice above")}: <code>{draft.landingPath}</code>
                            <Button size={"small"} onClick={() => setDraft({...draft, landingPath: undefined})}>
                                {t("Remove")}
                            </Button>
                        </Alert>}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t("Cancel")}</Button>
                <Button
                    variant={"contained"}
                    disabled={!valid || saving}
                    onClick={() => onSave({
                        ...draft,
                        name: draft.name?.trim(),
                        workstationName: draft.workstationName?.trim()
                    })}
                >
                    {device.id ? t("Save") : t("Create and show key")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/**
 * Shown once, right after a key is created or rotated — the plaintext cannot be fetched again.
 */
function PosTillKeyDialog({device, onClose}: { device: PosDevice, onClose: () => void }) {
    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();

    const snippet = buildTillsSnippet({[device.workstationName || "WORKSTATION-NAME"]: device.deviceKey ?? ""});

    const copy = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            enqueueSnackbar(t("Copied"), {variant: "success"});
        } catch {
            enqueueSnackbar(t("Copy failed — select the text and copy it manually."), {variant: "warning"});
        }
    };

    return (
        <Dialog open fullWidth maxWidth={"md"}>
            <DialogTitle>{t("Till key for")} {device.name}</DialogTitle>
            <DialogContent>
                <Stack gap={2}>
                    <Alert severity={"warning"}>
                        {t("Copy it now — this key is shown only once. If it is lost, issue a new key.")}
                    </Alert>
                    <Typography variant={"body2"}>
                        {t("In EMC, open Setup → Extension Application → CRQLAR and paste this as the content of \"crqlarTills\" (23 - Text). If the property already has tills there, add this line inside \"tills\" instead of replacing them. Then restart the POS client.")}
                    </Typography>
                    <Box
                        component={"pre"}
                        sx={{
                            m: 0, p: 2, borderRadius: 1, bgcolor: "action.hover",
                            fontFamily: "monospace", fontSize: 13, whiteSpace: "pre-wrap", wordBreak: "break-all"
                        }}
                    >
                        {snippet}
                    </Box>
                    <Stack direction={"row"} gap={1}>
                        <Button variant={"contained"} startIcon={<ContentCopyIcon/>} onClick={() => copy(snippet)}>
                            {t("Copy EMC snippet")}
                        </Button>
                        <Button startIcon={<ContentCopyIcon/>} onClick={() => copy(device.deviceKey ?? "")}>
                            {t("Copy key only")}
                        </Button>
                    </Stack>
                    <Typography variant={"body2"} color={"text.secondary"}>
                        {t("To test without a till, open")}{" "}
                        <code>{`${window.location.origin}/pos?k=${device.deviceKey}`}</code>{" "}
                        {t("in a private browser window.")}
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant={"contained"} onClick={onClose}>{t("I have copied it")}</Button>
            </DialogActions>
        </Dialog>
    );
}

export default SimphonyPosTills;
